import { FC } from 'react';
import { IonContent } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { Modal } from '../../components/Modal';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { IOutcomeClient, IOutcomeDefinition } from '@innerhive-internal/innerhive-common';
import { ClientAvatar } from './ClientAvatar';
import { OutcomeDefinition } from './OutcomeDefinition';

const useStyles = createUseStyles({
    body: {
        padding: '2px 18px 24px'
    },

    blurb: {
        marginBottom: 14,
        fontSize: 12.5,
        lineHeight: 1.55,
        color: CHART_INK.label
    },

    row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 11,
        width: '100%',
        padding: '11px 4px',
        border: 'none',
        borderTop: `1px solid ${BRAND.tealEdge}`,
        background: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:first-child': {
            borderTop: 'none'
        },

        '&:hover': {
            background: BRAND.tealWash
        }
    },

    who: {
        flex: 1,
        minWidth: 0
    },

    name: {
        display: 'block',
        fontSize: 14,
        fontWeight: 500,
        color: BRAND.deepTeal
    },

    detail: {
        display: 'block',
        marginTop: 2,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: CHART_INK.label
    },

    drivers: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 6,
        marginTop: 7
    },

    driver: {
        padding: '3px 9px',
        borderRadius: 999,
        border: `1px solid ${BRAND.tealEdge}`,
        background: BRAND.tealWash,
        fontSize: 11.5,
        lineHeight: 1.35,
        color: BRAND.deepTeal
    },

    driversLabel: {
        display: 'block',
        marginTop: 12,
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.03em',
        textTransform: 'uppercase' as const,
        color: CHART_INK.label
    },

    empty: {
        padding: '18px 2px',
        fontSize: 13,
        lineHeight: 1.55,
        color: CHART_INK.label
    }
});

export interface IInsightsClientList {
    title: string;
    blurb: string;
    clients: IOutcomeClient[];
    driversLabel?: string;
    /** Present when the list is the people behind an outcome figure. */
    definition?: IOutcomeDefinition;
}

/**
 * The names behind a figure.
 *
 * Every number on this tab is a count of people, and a count of people that cannot be opened is a
 * number nobody can act on: a dispatcher reading "2 clients came back to baseline" needs to know which
 * two before it changes anything they do. One modal serves every figure because the answer is always
 * the same shape, and because a reader who has opened one has learned how to open all of them.
 *
 * A row opens that client, so the drill-down is a way in rather than a dead end.
 */
export const InsightsClientListModal: FC<{
    /** Absent when nothing is open. Held by the caller so Ionic can animate the dismissal. */
    open?: IInsightsClientList;
    onSelectClient: (careMapId: string) => void;
    onClose: () => void;
}> = ({ open, onSelectClient, onClose }) => {
    const classes = useStyles();

    return (
        <Modal
            isOpen={Boolean(open)}
            title={open?.title ?? ''}
            data-id="insights-client-list-modal"
            desktopMaxWidth={520}
            closeHandler={onClose}
        >
            <IonContent>
                {open && (
                    <div className={classes.body}>
                        <div className={classes.blurb}>{open.blurb}</div>

                        {open.definition && <OutcomeDefinition definition={open.definition} />}

                        {open.clients.length === 0
                            ? <div className={classes.empty}>Nobody on this list right now.</div>
                            : open.clients.map(client => (
                                <button
                                    key={client.careMapId}
                                    type="button"
                                    className={classes.row}
                                    data-id="insights-client-list-row"
                                    data-caremap-id={client.careMapId}
                                    onClick={() => onSelectClient(client.careMapId)}
                                >
                                    <ClientAvatar careMapId={client.careMapId} clientName={client.clientName} size={28} />
                                    <span className={classes.who}>
                                        <span className={`${classes.name} mp-mask`}>{client.clientName}</span>
                                        <span className={`${classes.detail} mp-mask`}>{client.detail}</span>

                                        {client.drivers.length > 0 && (
                                            <>
                                                <span className={classes.driversLabel}>
                                                    {open.driversLabel ?? 'What was recorded'}
                                                </span>
                                                <span className={classes.drivers}>
                                                    {client.drivers.map((driver, index) => (
                                                        <span key={`${client.careMapId}-${index}`} className={`${classes.driver} mp-mask`}>{driver}</span>
                                                    ))}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </button>
                            ))}
                    </div>
                )}
            </IonContent>
        </Modal>
    );
};
